import { useQuery } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import queryString from 'query-string';
import { apiClient } from 'lib/api';
import type { QueryConfig } from 'lib/react-query';
import queryKeys from './queryKeys';
import type { TwitterCampaignWithLineItems } from './types';

export interface GetTwitterLineItemsParams {
  accountId: string;
  campaignIds: string[];
}

export const getTwitterLineItems = async (
  { params }: { params: GetTwitterLineItemsParams },
  signal?: AbortSignal,
): Promise<TwitterCampaignWithLineItems[]> => {
  const res = await apiClient.get<TwitterCampaignWithLineItems[]>(queryKeys.lineItems, {
    signal,
    params: {
      account_id: params.accountId,
      campaign_ids: params.campaignIds,
    },
    paramsSerializer: (prms) => queryString.stringify(prms, { arrayFormat: 'comma' }),
  });

  if (isAxiosError(res)) {
    const error = new Error('Get X Line Items Failed');
    error.cause = res.response?.data;
    return Promise.reject(error);
  }

  return res.data;
};

interface UseTwitterLineItemsOptions<TData> {
  params: GetTwitterLineItemsParams;
  config?: QueryConfig<typeof getTwitterLineItems, TData>;
}

export const useTwitterLineItems = <TData = TwitterCampaignWithLineItems[]>({
  params,
  config,
}: UseTwitterLineItemsOptions<TData>) =>
  useQuery({
    staleTime: 0,
    refetchOnMount: true,
    retry: false,
    ...config,
    enabled: (config?.enabled ?? true) && !!params.accountId && params.campaignIds.length > 0,
    queryFn: ({ signal }) => getTwitterLineItems({ params }, signal),
    queryKey: [queryKeys.lineItems, params.accountId, params.campaignIds],
  });
